import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { useT } from '../i18n';
import { listArticles, formatPublishedAt } from '../lib/magazineApi';

/*
  Insights — homepage section that surfaces the latest Magazine articles.
  Wired directly to /api/magazine/articles so the homepage stays in sync
  with the editorial calendar without manual updates.
*/

export default function Insights() {
  const { t, lang } = useT();
  const es = lang === 'es';
  const [items, setItems] = useState([]);
  const [ready, setReady] = useState(false);

  const CATEGORIES = [
    { slug: 'restaurant-ai', label: 'Restaurant AI' },
    { slug: 'restaurant-technology', label: es ? 'Tecnología' : 'Technology' },
    { slug: 'operations', label: es ? 'Operaciones' : 'Operations' },
    { slug: 'revenue-growth', label: es ? 'Crecimiento' : 'Revenue Growth' },
    { slug: 'case-studies', label: es ? 'Casos de Estudio' : 'Case Studies' },
    { slug: 'industry-news', label: es ? 'Noticias del Sector' : 'Industry News' },
  ];

  useEffect(() => {
    listArticles({ page_size: 3 })
      .then((res) => {
        setItems(res.items || []);
      })
      .catch(() => setItems([]))
      .finally(() => setReady(true));
  }, []);

  if (ready && items.length === 0) {
    // Render nothing if the Magazine is empty — keeps the homepage clean
    // instead of showing an empty section.
    return null;
  }

  return (
    <section className="py-20 lg:py-24 bg-white" id="magazine" data-testid="home-insights">
      <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
        <div className="mb-12 max-w-3xl">
          <p className="mb-4 inline-flex items-center gap-2.5">
            <span className="kx-eyebrow">{es ? 'Inteligencia Reciente' : 'Latest Intelligence'}</span>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-[10px] tracking-[0.16em] uppercase font-bold px-2.5 py-0.5">
              <span className="relative flex w-1.5 h-1.5">
                <span className="absolute inset-0 rounded-full bg-emerald-500 animate-ping opacity-60" />
                <span className="relative w-1.5 h-1.5 rounded-full bg-emerald-500" />
              </span>
              {es ? 'Auto-actualizado' : 'Auto-updating'}
            </span>
          </p>
          <h2 className="text-3xl sm:text-4xl font-semibold text-neutral-900 tracking-[-0.015em]">
            {t('insights.titleA')} <span className="text-[#F77F11]">{t('insights.titleAccent')}</span>
          </h2>
          <p className="mt-4 text-neutral-600 text-[15.5px] leading-relaxed">{t('insights.body')}</p>
          <div className="mt-6 flex flex-wrap gap-2" data-testid="home-insights-categories">
            {CATEGORIES.map((c) => (
              <Link
                key={c.slug}
                to={`/magazine/category/${c.slug}`}
                className="inline-flex items-center rounded-full border border-neutral-200 bg-white px-3.5 py-1.5 text-[12.5px] font-semibold text-neutral-600 hover:border-[#F77F11] hover:text-[#F77F11] transition-colors duration-200"
                data-testid={`home-insights-cat-${c.slug}`}
              >
                {c.label}
              </Link>
            ))}
          </div>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-7">
          {items.map((p) => (
            <Link
              key={p.slug}
              to={`/magazine/${p.slug}`}
              className="group rounded-2xl overflow-hidden border border-neutral-200 bg-white hover:border-orange-200 hover:shadow-[0_22px_55px_-25px_rgba(247,127,17,0.35)] hover:-translate-y-0.5 transition-all block"
              data-testid={`home-insights-card-${p.slug}`}
            >
              <div className="aspect-[4/3] overflow-hidden bg-neutral-100">
                {p.cover_image && (
                  <img
                    src={p.cover_image}
                    alt={p.cover_alt || p.title}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-500"
                  />
                )}
              </div>
              <div className="p-5">
                <div className="flex items-center gap-2 text-[11px] text-neutral-500 mb-2 font-medium">
                  {p.category && (
                    <span
                      className="tracking-[0.14em] uppercase font-bold"
                      style={{ color: p.category.accent_color || '#F77F11' }}
                    >
                      {p.category.name}
                    </span>
                  )}
                  {p.published_at && <span className="w-1 h-1 rounded-full bg-neutral-300" />}
                  {p.published_at && <span>{formatPublishedAt(p.published_at)}</span>}
                  <span className="w-1 h-1 rounded-full bg-neutral-300" />
                  <span>{p.reading_time} min</span>
                </div>
                <h3 className="font-semibold text-[17px] text-neutral-900 group-hover:text-[#F77F11] transition-colors leading-snug tracking-[-0.01em]">
                  {p.title}
                </h3>
                <p className="mt-2 text-[15px] text-neutral-600 leading-relaxed line-clamp-2">
                  {p.excerpt}
                </p>
                <span className="inline-flex items-center gap-1 mt-4 text-[14px] font-semibold text-[#F77F11] group-hover:text-[#d8651a]">
                  {t('insights.readMore')} <ArrowRight className="w-4 h-4" />
                </span>
              </div>
            </Link>
          ))}
        </div>

        <div className="mt-12 flex items-center justify-center">
          <Link
            to="/magazine"
            className="inline-flex items-center gap-1.5 text-[#F77F11] hover:text-[#d8651a] font-semibold text-sm"
            data-testid="home-insights-cta"
          >
            {t('insights.nextPage')} <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}
